import React from "react";
import { Share2, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const LifeInsuranceIn2026 = () => {
  const navigate = useNavigate();

  const blog = {
    title: "Understanding Life Insurance in 2026",
    author: "Vijay M D",
    date: "March 20, 2026",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85",
  };

  const handleShare = async () => {
    const shareData = {
      title: blog.title,
      text: "Check out this article",
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        alert("Link copied!");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Container>
      <Wrapper>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </BackButton>

        <Title>{blog.title}</Title>

        <Meta>
          {blog.date} • {blog.author}
        </Meta>

        <HeroImage src={blog.image} />

        <Content>
          <p>
            Life insurance in 2026 is no longer just about protection — it’s
            about smart financial planning. With the rise of digital platforms,
            buying and managing insurance has become faster, easier, and more
            transparent.
          </p>

          <h2>What is Life Insurance?</h2>
          <p>
            Life insurance is a contract between you and an insurer where your
            family receives financial support in case of your unfortunate death.
            It ensures that your loved ones are financially secure even when
            you're not around.
          </p>

          <h2>Why is it Important?</h2>
          <p>
            In today’s world, expenses are rising rapidly. From education to
            healthcare, financial responsibilities continue to grow. Life
            insurance acts as a safety net, ensuring your family can maintain
            their lifestyle.
          </p>

          <h2>Key Benefits</h2>
          <ul>
            <li>Financial protection for your family</li>
            <li>Tax benefits under applicable laws</li>
            <li>Peace of mind</li>
            <li>Affordable premiums (especially term insurance)</li>
          </ul>

          <h2>Choosing the Right Policy</h2>
          <p>
            When selecting a policy, consider your income, liabilities, future
            goals, and dependents. Term insurance is often the best starting
            point due to its simplicity and affordability.
          </p>

          <h2>Final Thoughts</h2>
          <p>
            Life insurance is not an expense — it’s an investment in your
            family’s future. The earlier you start, the better the benefits and
            lower the cost.
          </p>
        </Content>

        <Footer>
          <ShareButton onClick={handleShare}>
            <Share2 size={18} /> Share
          </ShareButton>
        </Footer>
      </Wrapper>
    </Container>
  );
};

export default LifeInsuranceIn2026;

export const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  padding: 100px 16px;
`;

export const Wrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: 20px;
  color: #555;

  &:hover {
    color: #000;
  }
`;

export const Title = styled.h1`
  font-size: 38px;
  font-weight: 700;
  margin-bottom: 10px;
`;

export const Meta = styled.p`
  color: #6b7280;
  margin-bottom: 20px;
`;

export const HeroImage = styled.img`
  width: 100%;
  border-radius: 14px;
  margin-bottom: 30px;
`;

export const Content = styled.div`
  font-size: 18px;
  line-height: 1.8;
  color: #374151;

  h2 {
    margin-top: 30px;
    margin-bottom: 10px;
    font-size: 24px;
  }

  ul {
    padding-left: 20px;
    margin-top: 10px;
  }

  li {
    margin-bottom: 6px;
  }
`;

export const Footer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
`;

export const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #000;
  color: #fff;
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  cursor: pointer;

  &:hover {
    background: #333;
  }
`;
